import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Settings,
  Building2,
  BarChart3,
  FileDown,
  FileText,
  Brain,
  Target,
} from 'lucide-react';

export function AdminDashboard() {
  const { profile, user } = useAuth();

  const adminSections = [
    {
      title: 'Bruker & Organisasjon',
      description: 'Administrer brukere, avdelinger og firmaer',
      links: [
        { name: 'Brukere', href: '/admin/users', icon: Users, description: 'Brukeradministrasjon' },
        { name: 'Avdelinger', href: '/admin/departments', icon: Building2, description: 'Avdelingsadministrasjon' },
        { name: 'Firmaer', href: '/admin/companies', icon: Building2, description: 'Firmaadministrasjon' },
      ]
    },
    {
      title: 'Systemadministrasjon',
      description: 'Avanserte systeminnstillinger og overvåking',
      links: [
        { name: 'Statistikk', href: '/admin/statistics', icon: BarChart3, description: 'Systemanalyse' },
        { name: 'Innstillinger', href: '/admin/settings', icon: Settings, description: 'Systemkonfigurasjon' },
        { name: 'Import/Eksport', href: '/admin/import-export', icon: FileDown, description: 'Verktøy for dataimport/-eksport' },
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
          Velkommen til Administrasjonsdashbord
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          Hei, {profile?.fullName || user?.email || 'Admin'}. Administrer Helsenøkkelen-systemet herfra.
        </p>
      </div>

      {/* Admin sections */}
      {adminSections.map((section) => (
        <div key={section.title} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{section.title}</h3>
            <p className="text-gray-600 dark:text-gray-400">{section.description}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.links.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className="group p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all duration-200 bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                      <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-900 dark:group-hover:text-blue-300">
                        {link.name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 mt-1">
                        {link.description}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      {/* Quick stats */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Systemoversikt</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Systemstatus</p>
                <p className="text-lg font-semibold text-blue-900 dark:text-blue-300">Aktiv</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Admintilgang</p>
                <p className="text-lg font-semibold text-green-900 dark:text-green-300">Aktivert</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Database</p>
                <p className="text-lg font-semibold text-purple-900 dark:text-purple-300">Tilkoblet</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              <div>
                <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">Rolle</p>
                <p className="text-lg font-semibold text-orange-900 dark:text-orange-300 capitalize">
                  {profile?.role?.replace('_', ' ') || 'Superadmin'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}